import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Check, X, Loader2, IndianRupee, Users, HardDrive, MessageSquare } from 'lucide-react';
import { planApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';

interface Plan {
  slug:          string;
  name:          string;
  tagline:       string;
  price_monthly: number;
  price_annual:  number;
  max_users:     number;
  storage_gb:    number;
  sms_monthly:   number;
  wa_monthly:    number;
  is_active:     boolean;
  sort_order:    number;
  features:      string[];
  updated_at:    string;
}

function paise(p: number) {
  return `₹${(p / 100).toLocaleString('en-IN')}`;
}

const fld = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange';

// ── Edit drawer ───────────────────────────────────────────────────
function EditDrawer({ plan, onClose }: { plan: Plan | null; onClose: () => void }) {
  const qc          = useQueryClient();
  const isNew       = plan === null;
  const [form, setForm] = useState<Partial<Plan & { featuresText: string }>>(() => ({
    slug:          isNew ? '' : plan!.slug,
    name:          isNew ? '' : plan!.name,
    tagline:       isNew ? '' : plan!.tagline,
    price_monthly: isNew ? 0  : plan!.price_monthly,
    max_users:     isNew ? 5  : plan!.max_users,
    storage_gb:    isNew ? 5  : plan!.storage_gb,
    sms_monthly:   isNew ? 0  : plan!.sms_monthly,
    wa_monthly:    isNew ? 0  : plan!.wa_monthly,
    sort_order:    isNew ? 99 : plan!.sort_order,
    featuresText:  isNew ? '' : (Array.isArray(plan!.features) ? plan!.features : []).join('\n'),
  }));
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      const features = (form.featuresText ?? '').split('\n').map(s => s.trim()).filter(Boolean);
      const payload  = {
        name:          form.name,
        tagline:       form.tagline,
        price_monthly: Number(form.price_monthly),
        max_users:     Number(form.max_users),
        storage_gb:    Number(form.storage_gb),
        sms_monthly:   Number(form.sms_monthly),
        wa_monthly:    Number(form.wa_monthly),
        sort_order:    Number(form.sort_order),
        features,
      };
      return isNew
        ? planApi.create({ ...payload, slug: form.slug })
        : planApi.update(plan!.slug, payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['plans'] }); onClose(); },
    onError:   (err: any) => setError(err.response?.data?.error ?? 'Save failed'),
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <div className="w-[480px] bg-white h-full overflow-y-auto flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-navy">{isNew ? 'New Plan' : `Edit — ${plan!.name}`}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-navy"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isNew && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Slug <span className="text-gray-300">(lowercase, no spaces)</span></label>
              <input value={form.slug} onChange={set('slug')} placeholder="enterprise" className={fld}/>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Display Name</label>
            <input value={form.name} onChange={set('name')} placeholder="Aarogya Enterprise" className={fld}/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tagline</label>
            <input value={form.tagline} onChange={set('tagline')} placeholder="For large hospitals…" className={fld}/>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Monthly price (paise)</label>
              <input type="number" value={form.price_monthly} onChange={set('price_monthly')} className={fld}/>
              <p className="text-xs text-gray-400 mt-1">{paise(Number(form.price_monthly ?? 0))}/month</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sort order</label>
              <input type="number" value={form.sort_order} onChange={set('sort_order')} className={fld}/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Max users</label>
              <input type="number" value={form.max_users} onChange={set('max_users')} className={fld}/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Storage (GB)</label>
              <input type="number" value={form.storage_gb} onChange={set('storage_gb')} className={fld}/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">SMS / month</label>
              <input type="number" value={form.sms_monthly} onChange={set('sms_monthly')} className={fld}/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">WhatsApp conv / month</label>
              <input type="number" value={form.wa_monthly} onChange={set('wa_monthly')} className={fld}/>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Feature bullets <span className="text-gray-300">(one per line)</span>
            </label>
            <textarea
              rows={8}
              value={form.featuresText}
              onChange={set('featuresText')}
              placeholder={'OPD Registration\nIPD Module\n…'}
              className={`${fld} font-mono text-xs resize-none`}
            />
          </div>

          {error && <p className="text-clinical-danger text-sm bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-500 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="flex-1 py-2 rounded-lg bg-orange text-navy text-sm font-semibold hover:bg-orange-dark
                       disabled:opacity-40 flex items-center justify-center gap-2">
            {mut.isPending ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
            {isNew ? 'Create Plan' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function PlansPage() {
  const { user }          = useAuthStore();
  const isSuperAdmin      = user?.role === 'super_admin';
  const qc                = useQueryClient();
  const [editing, setEditing] = useState<Plan | null | 'new'>('new' as any);
  const drawerOpen        = editing !== undefined && editing !== null;

  const { data, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn:  () => planApi.list().then(r => r.data.plans as Plan[]),
  });

  const toggleMut = useMutation({
    mutationFn: ({ slug, is_active }: { slug: string; is_active: boolean }) =>
      planApi.update(slug, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plans'] }),
  });

  // Reset drawer to nothing open
  const closeDrawer = () => setEditing(undefined as any);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-navy">Subscription Plans</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage pricing, features, and limits for each plan tier</p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setEditing(null)}
            className="flex items-center gap-2 px-4 py-2 bg-orange text-navy text-sm font-semibold
                       rounded-lg hover:bg-orange-dark transition-colors">
            <Plus size={15}/>
            New Plan
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-orange"/></div>
      ) : (
        <div className="grid grid-cols-1 gap-5">
          {(data ?? []).map(plan => (
            <div key={plan.slug}
              className={`bg-white rounded-xl border ${plan.is_active ? 'border-gray-200' : 'border-dashed border-gray-200 opacity-60'} p-5`}>

              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-navy text-lg">{plan.name}</h2>
                    {!plan.is_active && (
                      <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-0.5">{plan.tagline}</p>
                </div>

                <div className="text-right shrink-0">
                  <p className="text-2xl font-bold text-navy">{paise(plan.price_monthly)}</p>
                  <p className="text-xs text-gray-400">/ month</p>
                </div>
              </div>

              {/* Limits strip */}
              <div className="flex flex-wrap gap-4 mt-4 py-3 border-y border-gray-50">
                <Chip icon={<Users size={12}/>}
                  label={plan.max_users >= 999 ? 'Unlimited users' : `${plan.max_users} users`}/>
                <Chip icon={<HardDrive size={12}/>} label={`${plan.storage_gb} GB storage`}/>
                {plan.sms_monthly > 0 && (
                  <Chip icon={<MessageSquare size={12}/>} label={`${plan.sms_monthly.toLocaleString()} SMS/mo`}/>
                )}
                {plan.wa_monthly > 0 && (
                  <Chip icon={<MessageSquare size={12}/>} label={`${plan.wa_monthly.toLocaleString()} WA conv/mo`}/>
                )}
                <Chip icon={<IndianRupee size={12}/>} label={`slug: ${plan.slug}`} muted/>
              </div>

              {/* Features */}
              <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
                {(Array.isArray(plan.features) ? plan.features : []).map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                    <Check size={11} className="text-clinical-success mt-0.5 flex-shrink-0"/>
                    {f}
                  </li>
                ))}
              </ul>

              {/* Actions */}
              {isSuperAdmin && (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setEditing(plan)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                               border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors">
                    <Pencil size={12}/>
                    Edit
                  </button>
                  <button
                    onClick={() => toggleMut.mutate({ slug: plan.slug, is_active: !plan.is_active })}
                    disabled={toggleMut.isPending}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                                transition-colors ${plan.is_active
                                  ? 'border border-red-200 text-red-600 hover:bg-red-50'
                                  : 'border border-emerald-200 text-emerald-600 hover:bg-emerald-50'}`}>
                    {plan.is_active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit / New drawer */}
      {drawerOpen && (
        <EditDrawer
          plan={editing === null ? null : (editing as Plan)}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}

function Chip({ icon, label, muted }: { icon: React.ReactNode; label: string; muted?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full
                      ${muted ? 'bg-gray-50 text-gray-400' : 'bg-navy/5 text-navy'}`}>
      {icon}{label}
    </span>
  );
}
